// Remove Tool — paint over an area to remove it, filling with surrounding content.
// Photoshop 2023+ equivalent: Remove Tool (heals larger areas intelligently).

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { mergeBBox, type BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class RemoveTool implements Tool {
  readonly id = "remove";
  readonly shortcut = "J";

  size = 50;
  hardness = 0.5;
  sampleAllLayers = false;

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    removedMask: Uint8Array; // Track which pixels were marked for removal
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      removedMask: new Uint8Array(layer.pixels.width * layer.pixels.height),
    };

    this.stamp(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    const step = Math.max(1, this.size / 4);
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.stamp(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private stamp(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active) return;
    const { layer, layerSnapshot, removedMask } = this.active;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const radius = this.size / 2;
    const hardness = this.hardness;

    // Selection mask
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    const selMask = sel?.mask;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    // First pass: mark pixels for removal
    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= radius * radius) continue;

        if (selMask && selMask[py * w + px] === 0) continue;

        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const brushAlpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;

        const maskIdx = py * w + px;
        removedMask[maskIdx] = Math.max(removedMask[maskIdx]!, Math.round(brushAlpha * 255));
      }
    }

    // Second pass: fill marked pixels with surrounding content
    const fillRadius = Math.max(5, Math.round(this.size / 4));
    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        const maskIdx = py * w + px;
        if (removedMask[maskIdx] === 0) continue;

        const removalStrength = removedMask[maskIdx]! / 255;
        const dstIdx = maskIdx * 4;

        // Sample nearby non-removed pixels
        let r = 0, g = 0, b = 0, a = 0, totalWeight = 0;
        for (let fy = -fillRadius; fy <= fillRadius; fy++) {
          for (let fx = -fillRadius; fx <= fillRadius; fx++) {
            const nx = px + fx;
            const ny = py + fy;
            if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;

            const nMaskIdx = ny * w + nx;
            if (removedMask[nMaskIdx]! > 128) continue; // Skip removed pixels

            const nIdx = nMaskIdx * 4;
            if (layerSnapshot[nIdx + 3] === 0) continue; // Skip transparent

            const dist = Math.sqrt(fx * fx + fy * fy);
            const weight = 1 / (1 + dist * dist);
            r += layerSnapshot[nIdx]! * weight;
            g += layerSnapshot[nIdx + 1]! * weight;
            b += layerSnapshot[nIdx + 2]! * weight;
            a += layerSnapshot[nIdx + 3]! * weight;
            totalWeight += weight;
          }
        }

        if (totalWeight > 0) {
          const fillR = Math.round(r / totalWeight);
          const fillG = Math.round(g / totalWeight);
          const fillB = Math.round(b / totalWeight);
          const fillA = Math.round(a / totalWeight);

          // Blend based on removal strength
          data[dstIdx] = Math.round(layerSnapshot[dstIdx]! * (1 - removalStrength) + fillR * removalStrength);
          data[dstIdx + 1] = Math.round(layerSnapshot[dstIdx + 1]! * (1 - removalStrength) + fillG * removalStrength);
          data[dstIdx + 2] = Math.round(layerSnapshot[dstIdx + 2]! * (1 - removalStrength) + fillB * removalStrength);
          data[dstIdx + 3] = Math.round(layerSnapshot[dstIdx + 3]! * (1 - removalStrength) + fillA * removalStrength);

          if (px < touchX0) touchX0 = px;
          if (py < touchY0) touchY0 = py;
          if (px > touchX1) touchX1 = px;
          if (py > touchY1) touchY1 = py;
        }
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
